import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Plus, Pencil, Trash2, Phone, Users, UserPlus, Loader2, Globe } from "lucide-react";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function StaffActivities() {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  
  const [form, setForm] = useState({
    date: new Date(),
    calls_made: 0,
    potential_leads: 0,
    signups: 0,
    notes: "",
  });

  const fetchActivities = async () => {
    try {
      const response = await api().get("/activities");
      setActivities(response.data);
    } catch (error) {
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const resetForm = () => {
    setForm({
      date: new Date(),
      calls_made: 0,
      potential_leads: 0,
      signups: 0,
      notes: "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      date: format(form.date, "yyyy-MM-dd"),
      calls_made: parseInt(form.calls_made) || 0,
      potential_leads: parseInt(form.potential_leads) || 0,
      signups: parseInt(form.signups) || 0,
      notes: form.notes,
    };

    try {
      if (editingId) {
        await api().put(`/activities/${editingId}`, payload);
        toast.success("Activity updated!");
      } else {
        await api().post("/activities", payload);
        toast.success("Activity logged!");
      }
      setDialogOpen(false);
      resetForm();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save activity");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (activity) => {
    setForm({
      date: new Date(activity.date),
      calls_made: activity.calls_made,
      potential_leads: activity.potential_leads,
      signups: activity.signups,
      notes: activity.notes || "",
    });
    setEditingId(activity.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api().delete(`/activities/${id}`);
      toast.success("Activity deleted");
      fetchActivities();
    } catch (error) {
      toast.error("Failed to delete activity");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="staff-activities">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Activity Log</h1>
          <p className="text-slate-500 mt-1">Track your daily calls, leads, and signups</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 btn-active" data-testid="add-activity-btn">
              <Plus className="mr-2 h-4 w-4" />
              Log Activity
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Activity" : "Log New Activity"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="activity-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(form.date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(date) => date && setForm({ ...form, date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="calls">Calls Made</Label>
                  <Input
                    id="calls"
                    type="number"
                    min="0"
                    value={form.calls_made}
                    onChange={(e) => setForm({ ...form, calls_made: e.target.value })}
                    data-testid="activity-calls-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="potential">Potential</Label>
                  <Input
                    id="potential"
                    type="number"
                    min="0"
                    value={form.potential_leads}
                    onChange={(e) => setForm({ ...form, potential_leads: e.target.value })}
                    data-testid="activity-potential-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signups">Signups</Label>
                  <Input
                    id="signups"
                    type="number"
                    min="0"
                    value={form.signups}
                    onChange={(e) => setForm({ ...form, signups: e.target.value })}
                    data-testid="activity-signups-input"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes about today's activities..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  data-testid="activity-notes-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={saving}
                data-testid="activity-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingId ? "Update Activity" : "Log Activity"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Phone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No activities logged</h3>
            <p className="text-slate-500 mt-1">Start by logging your first daily activity</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {activities.map((activity, index) => (
            <Card
              key={activity.id}
              className={`border-slate-200 card-hover animate-fade-in stagger-${Math.min(index + 1, 6)}`}
              data-testid={`activity-item-${activity.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="text-center p-3 bg-slate-100 rounded-lg min-w-[80px]">
                      <p className="text-xs text-slate-500 uppercase font-semibold">
                        {formatSydney(activity.date, "MMM")}
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatSydney(activity.date, "dd")}
                      </p>
                    </div>
                    
                    <div className="flex gap-6">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <Phone className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-lg font-bold text-slate-900">{activity.calls_made}</p>
                          <p className="text-xs text-slate-500">Calls</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-amber-50 rounded-lg">
                          <Users className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-lg font-bold text-slate-900">{activity.potential_leads}</p>
                          <p className="text-xs text-slate-500">Potential</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-50 rounded-lg">
                          <UserPlus className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-lg font-bold text-slate-900">{activity.signups}</p>
                          <p className="text-xs text-slate-500">Signups</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(activity)}
                      data-testid={`edit-activity-${activity.id}`}
                    >
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(activity.id)}
                      data-testid={`delete-activity-${activity.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                
                {activity.notes && (
                  <p className="mt-4 text-sm text-slate-600 border-t border-slate-100 pt-4">
                    {activity.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
